import { createProductsQuery } from "@/abilities/product-and-asset/graphql/products.query";
import { generateDynamicSearchProductsOnlyAggregationsQuery } from "@/abilities/product-and-asset/helpers/product-asset-search-helper";
import { createDataSource, createTiles } from "@/abilities/product-and-asset/pages/products-overview.model";
import { convertAggregationsResult } from "@/shared/aggregations/aggregations-resolver";
import { SortEntry } from "@/shared/components/sort-dropdown/sort-dropdown.model";
import { useDataDisplayConfig } from "@/shared/data-display-config/composable/data-display-config";
import { DataDisplayConfigId } from "@/shared/data-display-config/composable/data-display.model";
import { INIT_LOAD_AMOUNT } from "@/shared/environment/consts.ts";
import { getSortEntries } from "@/shared/environment/filter/filter-types";
import { generateOrGroupsByFacetsSelected } from "@/shared/facets/facets-filter-generator";
import { Facet, FacetSelected } from "@/shared/facets/facets.model";
import { localizeSearchFields } from "@/shared/search/localize-search-fields";
import { DataSearchConfigId, useSearchConfig } from "@/shared/search/search-config";
import { generateSearchFieldsFilterClauseRegex } from "@/shared/search/search-fields-filter-clause";
import { SearchContext } from "@/shared/search/search.types";
import { consumerClient } from "@/shared/services/apollo-clients/consumer-client";
import { ProductQueryResult } from "@/shared/services/graphql/generated/admin-graph-types";
import { InputMaybe, ProductFilter } from "@/shared/services/graphql/generated/consumer-graph-types";
import { useLanguageStore } from "@/shared/store/language-store";
import { useQuery } from "@vue/apollo-composable";
import { computed, Ref } from "vue";
import { useI18n } from "vue-i18n";

export const useProductDataSource = (
    context: Ref<SearchContext>,
    emitSelectionInTiles: boolean,
    showButtons: boolean,
    selected: Ref<FacetSelected[]>,
    sort: Ref<SortEntry | undefined>,
    facet: Ref<Facet | undefined>,
    productIds: Ref<string[] | undefined>
) => {
    const { t } = useI18n();
    const languageStore = useLanguageStore();
    const { result: productSearchConfigResult } = useSearchConfig(DataSearchConfigId.product);

    function generateProductFilterVariables(
        searchContext: SearchContext,
        selected: FacetSelected[],
        sort: SortEntry | undefined
    ) {
        const searchPhrase = searchContext.text ?? "";
        const filteredSearchFields = localizeSearchFields(
            productSearchConfigResult.value?.searchEntries ?? [],
            languageStore.dataQueryLanguages
        );
        const searchFilterClause = generateSearchFieldsFilterClauseRegex(searchPhrase, filteredSearchFields, true);

        const productFilter: ProductFilter = {};

        //The wildcard search with * behaves like empty-string search, otherwise the regex will not fit and no results will be returned (see QIT-2531).
        if (searchContext.text && searchContext.text !== "*") {
            productFilter.orGroup = searchFilterClause.orGroup;
        }

        const copySelected = [...selected];
        const orGroups = generateOrGroupsByFacetsSelected(copySelected);

        if (orGroups.length > 0) {
            productFilter.andGroup = orGroups;
        }

        if (productIds.value && productIds.value.length > 0) {
            productFilter.ids = productIds.value;
        }

        const sortEntries = getSortEntries(sort, languageStore.dataQueryLanguages);

        return {
            productFilter,
            acceptedLanguages: languageStore.dataQueryLanguages,
            sortEntries,
        };
    }

    const productsQueryVariables = computed<{ filter: InputMaybe<ProductFilter> } | undefined>(() => {
        const productFilter = generateProductFilterVariables(context.value, selected.value, sort.value);
        return {
            filter: productFilter.productFilter as ProductFilter,
        };
    });

    const aggregationsFilter = computed<{ filter: InputMaybe<ProductFilter> } | undefined>(() => {
        //remove own facet from selected
        const selectedCopy = [...selected.value.filter((x) => x.referencedId !== facet.value?.referencedId)];
        const productFilter = generateProductFilterVariables(context.value, selectedCopy, sort.value);
        return {
            filter: productFilter.productFilter as ProductFilter,
        };
    });

    const {
        result: dataDisplayResult,
        loading: dataConfigLoading,
        error: dataError,
    } = useDataDisplayConfig(DataDisplayConfigId.productSelection, t);

    const queryDocument = computed(() => {
        if (dataDisplayResult.value)
            return createProductsQuery(
                dataDisplayResult.value.datafieldsFragment.datafields,
                dataDisplayResult.value.datafieldsFragment.localizationDatafields
            );

        return createProductsQuery({}, {});
    });

    const productsFilter = computed(() => {
        return {
            ...productsQueryVariables.value,
            first: INIT_LOAD_AMOUNT,
            acceptedLanguages: languageStore.dataQueryLanguages,
            sort: getSortEntries(sort.value, languageStore.dataQueryLanguages),
        };
    });

    const {
        loading: productsLoading,
        error: productsError,
        result,
        fetchMore,
        refetch: refetchQuery,
    } = useQuery(
        queryDocument,
        productsFilter,
        computed(() => ({
            enabled: !!(dataDisplayResult.value && sort.value),
        }))
    );

    const loading = computed(() => {
        return productsLoading.value || dataConfigLoading.value || !sort.value;
    });

    const error = computed(() => {
        return dataError.value || productsError.value;
    });

    const products = computed(() => {
        return result.value?.products;
    });

    const total = computed(() => {
        return result.value?.products.total;
    });

    const dataSource = createDataSource(emitSelectionInTiles, showButtons);

    const tiles = computed(() => {
        return createTiles(dataSource, products.value ? (products.value as ProductQueryResult).products : []);
    });

    const fetchAggregation = async (searchPhrase: string) => {
        if (!facet.value) return {};

        const aggregationResult = await consumerClient.query({
            query: generateDynamicSearchProductsOnlyAggregationsQuery(facet.value, searchPhrase),
            variables: aggregationsFilter.value,
        });

        const convertedAggregations = convertAggregationsResult(
            facet.value,
            aggregationResult.data["products"].aggregations
        );

        return convertedAggregations[facet.value.id];
    };

    const refetch = async () => {
        await refetchQuery(productsFilter.value);
    };

    return {
        loading,
        error,
        tiles,
        products,
        fetchMore,
        total,
        fetchAggregation,
        refetch,
    };
};
